import { useState } from 'react';
import { useRouter } from 'next/router';
import { supabase } from '../../lib/supabaseClient';
import AdminLayout from '../../src/components/AdminLayout';
import styles from '../../styles/Admin.module.css';

export default function AdminAddGame() {
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Form State
  const [formData, setFormData] = useState({
    slug: '',
    game_name: '',
    title: '',
    release_date: '',
    brand: '',
    is_hot: false,
    label: '',
    tags: '',
    orientation: 'any',
    thumb: '',
    thumb_big: '',
    iframe: '',
    video: '',
    s_dis: '',
    l_dis: '',
    details: '{}',
    faq: '[]'
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const generateSlug = () => {
    if (formData.game_name) {
      const newSlug = formData.game_name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)+/g, '');
      setFormData(prev => ({ ...prev, slug: newSlug }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Prepare data for Supabase
    let parsedDetails = {};
    let parsedFaq = [];
    try {
      parsedDetails = JSON.parse(formData.details || '{}');
      parsedFaq = JSON.parse(formData.faq || '[]');
    } catch (err) {
      setError("Invalid JSON format in Details or FAQ fields.");
      setLoading(false);
      return;
    }

    const tagsArray = formData.tags
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag !== '');

    const gameData = {
      slug: formData.slug,
      game_name: formData.game_name,
      title: formData.title,
      release_date: formData.release_date,
      brand: formData.brand,
      is_hot: formData.is_hot,
      label: formData.label,
      tags: tagsArray,
      orientation: formData.orientation,
      thumb: formData.thumb,
      thumb_big: formData.thumb_big,
      iframe: formData.iframe,
      video: formData.video,
      s_dis: formData.s_dis,
      l_dis: formData.l_dis,
      details: parsedDetails,
      faq: parsedFaq
    };

    const { error: insertError } = await supabase
      .from('bestgamespot_games')
      .insert([gameData]);

    if (insertError) {
      setError(insertError.message);
    } else {
      alert("Game added successfully!");
      router.push('/admin');
    }
    
    setLoading(false);
  };

  return (
    <AdminLayout>
      <div className={styles.headerRow}>
        <h1>Add New Game</h1>
      </div>
      
      {error && <div className={styles.error}>{error}</div>}

      <form onSubmit={handleSubmit} className={styles.formGrid}>
        
        {/* Row 1 */}
        <div className={styles.inputGroup}>
          <label>Game Name *</label>
          <input type="text" name="game_name" value={formData.game_name} onChange={handleChange} required onBlur={generateSlug} />
        </div>
        <div className={styles.inputGroup}>
          <label>Slug (URL) *</label>
          <input type="text" name="slug" value={formData.slug} onChange={handleChange} required />
        </div>

        {/* Row 2 */}
        <div className={styles.inputGroup}>
          <label>SEO Title</label>
          <input type="text" name="title" value={formData.title} onChange={handleChange} />
        </div>
        <div className={styles.inputGroup}>
          <label>Category (Label) *</label>
          <input type="text" name="label" value={formData.label} onChange={handleChange} required />
        </div>

        {/* Row 3 */}
        <div className={styles.inputGroup}>
          <label>Tags (comma separated)</label>
          <input type="text" name="tags" value={formData.tags} onChange={handleChange} placeholder="e.g. Action, 3D, Racing" />
        </div>
        <div className={styles.inputGroup}>
          <label>Brand / Publisher</label>
          <input type="text" name="brand" value={formData.brand} onChange={handleChange} />
        </div>

        {/* Row 4 */}
        <div className={styles.inputGroup}>
          <label>Release Date</label>
          <input type="date" name="release_date" value={formData.release_date} onChange={handleChange} />
        </div>
        <div className={styles.inputGroup}>
          <label>Orientation</label>
          <select name="orientation" value={formData.orientation} onChange={handleChange}>
            <option value="any">Any</option>
            <option value="landscape">Landscape</option>
            <option value="portrait">Portrait</option>
          </select>
        </div>

        {/* Row 5 */}
        <div className={styles.inputGroup}>
          <label>Is Hot?</label>
          <input type="checkbox" name="is_hot" checked={formData.is_hot} onChange={handleChange} style={{width:'auto'}} />
        </div>
        <div className={styles.inputGroup}></div> {/* Empty spacer */}

        {/* URLs */}
        <div className={styles.inputGroup + ' ' + styles.formFullWidth}>
          <label>Iframe URL (Game Link) *</label>
          <input type="url" name="iframe" value={formData.iframe} onChange={handleChange} required />
        </div>
        <div className={styles.inputGroup}>
          <label>Thumbnail URL (Small)</label>
          <input type="url" name="thumb" value={formData.thumb} onChange={handleChange} />
        </div>
        <div className={styles.inputGroup}>
          <label>Thumbnail URL (Big)</label>
          <input type="url" name="thumb_big" value={formData.thumb_big} onChange={handleChange} />
        </div>
        <div className={styles.inputGroup + ' ' + styles.formFullWidth}>
          <label>Gameplay Video URL (MP4)</label>
          <input type="url" name="video" value={formData.video} onChange={handleChange} />
        </div>

        {/* Descriptions */}
        <div className={styles.inputGroup + ' ' + styles.formFullWidth}>
          <label>Short Description (s_dis)</label>
          <textarea name="s_dis" value={formData.s_dis} onChange={handleChange} rows="3" />
        </div>
        <div className={styles.inputGroup + ' ' + styles.formFullWidth}>
          <label>Long Description (l_dis)</label>
          <textarea name="l_dis" value={formData.l_dis} onChange={handleChange} rows="6" />
        </div>

        {/* JSON Fields */}
        <div className={styles.inputGroup}>
          <label>Details (JSON format)</label>
          <textarea name="details" value={formData.details} onChange={handleChange} rows="5" />
          <small style={{color: '#888'}}>Example: {`{"Developer": "Studio X", "Platform": "Web Browser"}`}</small>
        </div>
        <div className={styles.inputGroup}>
          <label>FAQ (JSON Array format)</label>
          <textarea name="faq" value={formData.faq} onChange={handleChange} rows="5" />
          <small style={{color: '#888'}}>Example: {`[{"question": "How to play?", "answer": "Use arrows"}]`}</small>
        </div>

        <div className={styles.formFullWidth}>
          <button type="submit" className={styles.saveBtn} disabled={loading}>
            {loading ? 'Saving Game...' : 'Save New Game'}
          </button>
        </div>

      </form>
    </AdminLayout>
  );
}
